import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Items } from "assets/SideBarIcons/items.svg";
import { useGetItemHistoryService } from 'subModule/src/services/items/useItems';
import ItemTabs from './ItemTabs';
import { Button, CircularProgress, Grid, SelectChangeEvent } from '@mui/material';
import ItemsHistorySearchHeader from 'components/Items/ItemsHistorySearchHeader';
import { CommonProfileService } from 'subModule/src/core/services';
import { useSelector } from 'react-redux';
import { brandingInfo } from 'features/server/brandingSlice';
import { themes } from 'theme/themes';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';
import { ItemsProfileService } from 'subModule/src/core/services';

interface SearchFormData {
    [key: string]: string | any;
}

export default function ItemsHistoryTable() {
    const params = useParams();
    const itemId = params?.id;
    const navigate = useNavigate();
    const [itemTitle, setItemTitle] = useState('');
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string[]>(['referenceNo', 'dateInterval']);
    const [search, setSearch] = useState<any>({});
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ referenceNo: '', startDate: null, endDate: null });
    const { data: tableData, isFetching, refetch } = useGetItemHistoryService({ pageNumber: pageNo, size: pageSize, ...search }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        if (!itemId) return;
        let active = true;
        ItemsProfileService.getItemByID(+itemId)
            .then((res: unknown) => {
                if (!active) return;
                const name = pickProfileEntityTitle(res);
                if (name) setItemTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [itemId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'items', href: '/parts' },
            {
                labelKey: 'title',
                ...(itemTitle ? { label: itemTitle } : {}),
                href: itemId ? `/part/${itemId}` : undefined,
            },
            { labelKey: 'history' },
        ],
        [itemId, itemTitle],
    );

    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search]);
    useEffect(() => {
        if (loading) handleSubmitSearch();
    }, [searchFormData.startDate, searchFormData.endDate]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const clearSearch = () => {
        setFilter(['referenceNo', 'dateInterval']);
        setSearchFormData({ referenceNo: '', startDate: null, endDate: null });
        if (Object.keys(search).length) {
            setSearch({});
        }
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        setFilter(typeof value === 'string' ? value.split(',') : value,);
        setSearchFormData(prevFormData => {
            const selectedFilters = typeof value === 'string' ? value.split(',') : value;
            const updatedFormData = Object.fromEntries(Object.keys(prevFormData).map(key =>
                [key, value.includes(key) ? prevFormData[key] : '']
            ));
            if (selectedFilters.includes('dateInterval')) {
                updatedFormData.startDate = prevFormData.startDate;
                updatedFormData.endDate = prevFormData.endDate;
            }
            return updatedFormData;
        });
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        if (field === 'dateInterval') {
            setSearchFormData((prevData) => ({
                ...prevData,
                startDate: value[0],
                endDate: value[1]
            }));
            if ((value[0] && value[1]) || ((value[0] === null) && (value[1] === null))) {
                setLoading(true);
            }
        } else {
            setSearchFormData((prevData) => ({
                ...prevData,
                [field]: value,
            }));
        }
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string> = {};
        const { startDate, endDate, ...rest } = searchFormData;
        if (startDate && endDate) {
            filteredSearch.startDate = startDate;
            filteredSearch.endDate = endDate;
        } else {
            setSearchFormData((prev) => ({
                ...prev,
                startDate: null,
                endDate: null,
            }));
        }
        Object.entries(rest).forEach(([key, value]) => {
            if (value) {
                filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
            }
        });
        setSearch(filteredSearch);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const DownloadItemHistory = () => {
        CommonProfileService.exportEntityWithParams(
            { ...search },
            `items/item-history/${params?.id}/export/excel`
        )
            .then((res: Blob) => {
                const blob = new Blob([res], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `PartHistory-${tableData?.pages[0]?.data?.content[0]?.itemCode || params?.id}.xlsx`);
                document.body.appendChild(link);
                link.click();

                link.remove();
                window.URL.revokeObjectURL(url);
                enqueueSnackbar(
                    <AlertMessages text={t("downloadedSuccessfully")} />,
                    { variant: "success" }
                );
            })
            .catch((err) => {
                console.error("===", err);
                enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
            })
            .finally(() => {
                setDownloading(false);
            });
    };

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/parts')}
                heading={t('items')}
                icon={<Items height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <ItemTabs params={params} selectedIndex={5} /> : null}
                    <BackendPaginationTable
                        columns={[
                            { id: 'reference', label: 'reference', numeric: false },
                            { id: 'inQty', label: 'inQty', numeric: false },
                            { id: 'outQty', label: 'outQty', numeric: false },
                            { id: 'balance', label: 'balance', numeric: false },
                            { id: 'supplier', label: 'supplier', numeric: false },
                            { id: 'createdByName', label: 'createdBy', numeric: false },
                            { id: 'transaction', label: 'transaction', numeric: false },
                            { id: 'transactionDate', label: 'transactionDate', numeric: false },
                        ]}
                        data={tableData?.pages[0]?.data?.content || []}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        tableData={tableData?.pages[0]?.data || {}}
                        showCheckbox={false}
                        showActions={true}
                        enableSorting={true}
                        showHeader={true}
                        showSearch={true}
                        showFilter={true}
                        showCreateButton={false}
                        actions={[]}
                        handleChangePage={handleChangePage}
                        handleChangeRows={handleChangeRows}
                        loadingSearch={isFetching || loading}
                        headerComponent={<>
                            <ItemsHistorySearchHeader
                                searchFormData={searchFormData}
                                handleChangeFilter={handleChangeFilter}
                                handleChangeSearch={handleChangeSearch}
                                handleSubmitSearch={handleSubmitSearch}
                                clearSearch={clearSearch}
                                {...{ filter, search }}
                            />
                            {checkPrivileges("ROLE_ITEM_HISTORY_EXCEL_EXPORT") && <Grid item className="text-end">
                                <Button
                                    id="import-button"
                                    variant="contained"
                                    disableElevation
                                    onClick={() => { setDownloading(true); DownloadItemHistory(); }}
                                    className={`bg-white font-Saira font-[600] text-[13px] h-[31px] mt-4 px-4 py-1 cursor-pointer rounded-[6px] ms-[10px]`}
                                    sx={{
                                        outline: "2px solid",
                                        color: themeColors?.primary,
                                        borderColor: themeColors?.primary,
                                    }}
                                    disabled={downloading}
                                >
                                    {downloading ? (<CircularProgress
                                        sx={{ color: themeColors?.primary, marginRight: "8px" }} size={16} />
                                    ) : ""} {t("download")}
                                </Button>
                            </Grid>}
                        </>}
                    />
                </div>
            </Box>
        </Box>
    )
}
